import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useGoogleLogin } from '@react-oauth/google';
import { useAuth } from '../context/AuthContext';
import { clarityEvent } from '../utils/clarity';
import './AuthForm.css';

const MicrosoftIcon = () => (
    <svg width="18" height="18" viewBox="0 0 21 21" fill="none" aria-hidden="true">
        <rect x="1" y="1" width="9" height="9" fill="#F25022"/>
        <rect x="11" y="1" width="9" height="9" fill="#7FBA00"/>
        <rect x="1" y="11" width="9" height="9" fill="#00A4EF"/>
        <rect x="11" y="11" width="9" height="9" fill="#FFB900"/>
    </svg>
);

const GoogleIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
);

const Login = () => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError]       = useState('');
    const [loading, setLoading]   = useState(false);
    const navigate = useNavigate();

    const { login, loginWithMicrosoft, loginWithGoogle, user, mustChangePassword } = useAuth();

    useEffect(() => {
        if (user) {
            navigate(mustChangePassword ? '/force-password-change' : '/dashboard', { replace: true });
        }
    }, [user, mustChangePassword, navigate]);

    const googleLogin = useGoogleLogin({
        onSuccess: async (tokenResponse) => {
            setLoading(true);
            try {
                await loginWithGoogle(tokenResponse.access_token);
                clarityEvent('login_success_google');
            } catch (err) {
                const msg = err.response?.data || 'Google sign-in failed. Please try again.';
                setError(typeof msg === 'string' ? msg : 'Google sign-in failed. Please try again.');
                setLoading(false);
            }
        },
        onError: () => setError('Google sign-in failed. Please try again.'),
    });

    const handleMicrosoftLogin = async () => {
        setError('');
        setLoading(true);
        try {
            await loginWithMicrosoft();
        } catch {
            setError('Microsoft sign-in failed. Please try again.');
            setLoading(false);
        }
    };

    const handleEmailSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            await login(username, password);
            clarityEvent('login_success');
        } catch (err) {
            const msg = typeof err === 'string' ? err : err.message;
            setError(msg || 'Failed to log in. Please check your credentials.');
            setLoading(false);
        }
    };

    return (
        <div className="auth-container">
            {/* ── Brand panel ── */}
            <div className="auth-welcome-panel">
                <div className="welcome-content">
                    <div className="welcome-content__brand">
                        <span className="welcome-content__brand-name">eYantra</span>
                        <span className="welcome-content__brand-dot" />
                        <span className="welcome-content__brand-name" style={{ fontWeight: 400, opacity: 0.75 }}>
                            Commerce
                        </span>
                    </div>
                    <h1>Welcome Back!</h1>
                    <p>Log in to access your dashboard and explore our exclusive range of corporate merchandise.</p>
                    <div className="welcome-content__trust">
                        <div className="welcome-trust-item">
                            <div className="welcome-trust-item__icon">🚚</div>
                            <span>Ships Pan-India — All Major Cities Covered</span>
                        </div>
                        <div className="welcome-trust-item">
                            <div className="welcome-trust-item__icon">📦</div>
                            <span>Bulk Orders with Volume Pricing</span>
                        </div>
                        <div className="welcome-trust-item">
                            <div className="welcome-trust-item__icon">🎨</div>
                            <span>Custom Branding &amp; Logo Printing</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Form panel ── */}
            <div className="auth-form-panel">
                <div className="auth-form-container">
                    <Link to="/" className="back-home-link">← Back to Home</Link>

                    <h2>Login</h2>
                    <p className="form-subtitle">Enter your credentials to continue.</p>

                    {error && <p className="error-message" role="alert">{error}</p>}

                    {/* ── Email/password form ── */}
                    <form onSubmit={handleEmailSubmit} noValidate>
                        <div className="input-group">
                            <input
                                type="text"
                                className="auth-input"
                                placeholder="Username or Email"
                                value={username}
                                onChange={e => setUsername(e.target.value)}
                                required
                                autoComplete="username"
                            />
                        </div>
                        <div className="input-group">
                            <input
                                type="password"
                                className="auth-input"
                                placeholder="Password"
                                value={password}
                                onChange={e => setPassword(e.target.value)}
                                required
                                autoComplete="current-password"
                            />
                        </div>
                        <div className="auth-forgot-row">
                            <Link to="/forgot-password" className="auth-link">Forgot Password?</Link>
                        </div>
                        <button type="submit" className="auth-button-primary" disabled={loading}>
                            {loading ? 'Signing in…' : 'Login'}
                        </button>
                    </form>

                    {/* ── SSO options ── */}
                    <div className="auth-divider">or</div>

                    <button
                        type="button"
                        className="auth-button-microsoft"
                        onClick={handleMicrosoftLogin}
                        disabled={loading}
                    >
                        <MicrosoftIcon />
                        Continue with Microsoft
                    </button>

                    <button
                        type="button"
                        className="auth-button-microsoft"
                        onClick={() => googleLogin()}
                        disabled={loading}
                        style={{ marginTop: '0.625rem' }}
                    >
                        <GoogleIcon />
                        Continue with Google
                    </button>

                    <div className="form-links">
                        <p>Don't have an account? <Link to="/register" className="auth-link">Sign Up</Link></p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Login;